import { useState, useMemo } from "react";
import usePageLoader from '../../hooks/usePageLoader';
import { ShimmerBox, ShimmerCard } from '../../components/common/Shimmer';
import { FiSearch, FiX, FiChevronDown, FiChevronUp, FiExternalLink, FiCheck, FiZap } from "react-icons/fi";
import toast from "react-hot-toast";

const ALL_INTEGRATIONS = [
  /* ── CRM ─────────────────────────────────────────────────────────────── */
  {
    id: "hubspot", name: "HubSpot", category: "CRM",
    description: "Sync captured leads directly to HubSpot CRM contacts and deals.",
    color: "bg-orange-500", initials: "HS", connected: false, popular: true,
    fields: [{ key: "apiKey", label: "API Key", type: "password" }],
  },
  {
    id: "salesforce", name: "Salesforce", category: "CRM",
    description: "Push leads and conversations to Salesforce as contacts and opportunities.",
    color: "bg-blue-500", initials: "SF", connected: false,
    fields: [
      { key: "clientId", label: "Client ID", type: "text" },
      { key: "clientSecret", label: "Client Secret", type: "password" },
    ],
  },
  {
    id: "pipedrive", name: "Pipedrive", category: "CRM",
    description: "Create deals and contacts in Pipedrive from captured chat leads.",
    color: "bg-green-600", initials: "PD", connected: true,
    fields: [{ key: "apiToken", label: "API Token", type: "password" }],
  },
  {
    id: "zoho", name: "Zoho CRM", category: "CRM",
    description: "Sync leads and conversation data to Zoho CRM automatically.",
    color: "bg-red-500", initials: "ZC", connected: false,
    fields: [{ key: "apiKey", label: "API Key", type: "password" }],
  },
  {
    id: "activecampaign", name: "ActiveCampaign", category: "CRM",
    description: "Add contacts and trigger email automations from lead captures.",
    color: "bg-violet-600", initials: "AC", connected: false,
    fields: [
      { key: "apiUrl", label: "Account URL", type: "text", placeholder: "https://youracccount.api-us1.com" },
      { key: "apiKey", label: "API Key", type: "password" },
    ],
  },
  /* ── Messaging ────────────────────────────────────────────────────────── */
  {
    id: "slack", name: "Slack", category: "Messaging",
    description: "Send new lead notifications and chat summaries to any Slack channel.",
    color: "bg-indigo-600", initials: "SL", connected: false, popular: true,
    fields: [{ key: "webhookUrl", label: "Webhook URL", type: "text", placeholder: "https://hooks.slack.com/services/…" }],
  },
  {
    id: "whatsapp", name: "WhatsApp Business", category: "Messaging",
    description: "Deploy your chatbot on WhatsApp Business via the Cloud API.",
    color: "bg-green-500", initials: "WA", connected: false, isNew: true,
    fields: [
      { key: "accessToken", label: "Access Token", type: "password" },
      { key: "phoneId", label: "Phone Number ID", type: "text" },
    ],
  },
  {
    id: "messenger", name: "Facebook Messenger", category: "Messaging",
    description: "Answer Facebook Page messages automatically with your chatbot.",
    color: "bg-blue-600", initials: "FM", connected: false,
    fields: [{ key: "pageToken", label: "Page Access Token", type: "password" }],
  },
  {
    id: "telegram", name: "Telegram", category: "Messaging",
    description: "Add your chatbot to Telegram using the Bot API.",
    color: "bg-sky-500", initials: "TG", connected: false,
    fields: [{ key: "botToken", label: "Bot Token", type: "password", placeholder: "123456:ABC-DEF…" }],
  },
  {
    id: "intercom", name: "Intercom", category: "Messaging",
    description: "Sync leads and hand off conversations to Intercom agents.",
    color: "bg-blue-700", initials: "IC", connected: false,
    fields: [{ key: "accessToken", label: "Access Token", type: "password" }],
  },
  /* ── Automation ──────────────────────────────────────────────────────── */
  {
    id: "zapier", name: "Zapier", category: "Automation",
    description: "Trigger Zaps from new leads, messages, and chatbot events.",
    color: "bg-orange-600", initials: "ZP", connected: false, popular: true,
    fields: [{ key: "webhookUrl", label: "Zapier Webhook URL", type: "text", placeholder: "https://hooks.zapier.com/…" }],
  },
  {
    id: "make", name: "Make", category: "Automation",
    description: "Connect NexaGPT to 1,000+ apps via Make scenarios.",
    color: "bg-violet-500", initials: "MK", connected: false,
    fields: [{ key: "webhookUrl", label: "Make Webhook URL", type: "text" }],
  },
  {
    id: "n8n", name: "n8n", category: "Automation",
    description: "Self-hosted automation workflows triggered by chatbot events.",
    color: "bg-red-600", initials: "N8", connected: false, isNew: true,
    fields: [{ key: "webhookUrl", label: "n8n Webhook URL", type: "text" }],
  },
  /* ── E-commerce ──────────────────────────────────────────────────────── */
  {
    id: "shopify", name: "Shopify", category: "E-commerce",
    description: "Train on your product catalogue and answer order questions in real time.",
    color: "bg-green-700", initials: "SH", connected: false, popular: true,
    fields: [
      { key: "shopDomain", label: "Shop Domain", type: "text", placeholder: "yourstore.myshopify.com" },
      { key: "accessToken", label: "Admin API Token", type: "password" },
    ],
  },
  {
    id: "woocommerce", name: "WooCommerce", category: "E-commerce",
    description: "Connect your WooCommerce store for product and order support.",
    color: "bg-indigo-700", initials: "WC", connected: false,
    fields: [
      { key: "siteUrl", label: "Site URL", type: "text", placeholder: "https://yourstore.com" },
      { key: "consumerKey", label: "Consumer Key", type: "text" },
      { key: "consumerSecret", label: "Consumer Secret", type: "password" },
    ],
  },
  /* ── Webhooks ────────────────────────────────────────────────────────── */
  {
    id: "webhook", name: "Custom Webhook", category: "Webhooks",
    description: "Send lead data and conversation events to any URL via HTTP POST.",
    color: "bg-gray-700", initials: "WH", connected: false, popular: true,
    fields: [
      { key: "url", label: "Endpoint URL", type: "text", placeholder: "https://your-server.com/webhook" },
      { key: "secret", label: "Signing Secret (optional)", type: "password" },
    ],
  },
  {
    id: "gtm", name: "Google Tag Manager", category: "Webhooks",
    description: "Fire GTM events on lead capture and conversation milestones.",
    color: "bg-blue-700", initials: "GT", connected: false,
    fields: [{ key: "containerId", label: "Container ID", type: "text", placeholder: "GTM-XXXXXXX" }],
  },
  {
    id: "googleanalytics", name: "Google Analytics 4", category: "Webhooks",
    description: "Track chatbot interactions and conversion events in GA4.",
    color: "bg-yellow-500", initials: "GA", connected: false, isNew: true,
    fields: [{ key: "measurementId", label: "Measurement ID", type: "text", placeholder: "G-XXXXXXXXXX" }],
  },
];

const CATEGORIES = ["All", "CRM", "Messaging", "Automation", "E-commerce", "Webhooks"];

function IntegrationIcon({ color, initials }) {
  return (
    <div className={`w-10 h-10 rounded-xl ${color} flex items-center justify-center text-white font-bold text-xs shrink-0`}>
      {initials}
    </div>
  );
}

function IntegrationsSkeleton() {
  return (
    <div className="flex-1 overflow-y-auto bg-gray-50 px-6 py-5 space-y-6">
      <div className="flex items-center gap-3">
        <ShimmerBox h="h-9" w="w-64" rounded="rounded-lg" />
        <div className="flex gap-2">
          {[...Array(5)].map((_, i) => <ShimmerBox key={i} h="h-8" w="w-20" rounded="rounded-lg" />)}
        </div>
      </div>
      <div>
        <ShimmerBox h="h-4" w="w-32" className="mb-3" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => <ShimmerCard key={i} />)}
        </div>
      </div>
      <div>
        <ShimmerBox h="h-4" w="w-40" className="mb-3" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(9)].map((_, i) => <ShimmerCard key={i} />)}
        </div>
      </div>
    </div>
  );
}

export default function Integrations() {
  const [category, setCategory] = useState("All");
  const loading = usePageLoader();
  const [search, setSearch] = useState("");
  const [connected, setConnected] = useState(
    ALL_INTEGRATIONS.filter((i) => i.connected).map((i) => i.id)
  );
  const [expanding, setExpanding] = useState(null);
  const [formValues, setFormValues] = useState({});
  const [showTokens, setShowTokens] = useState({});

  const counts = useMemo(() => {
    const map = { All: ALL_INTEGRATIONS.length };
    CATEGORIES.slice(1).forEach((cat) => {
      map[cat] = ALL_INTEGRATIONS.filter((i) => i.category === cat).length;
    });
    return map;
  }, []);

  const filtered = useMemo(() => {
    let list = ALL_INTEGRATIONS;
    if (category !== "All") list = list.filter((i) => i.category === category);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (i) => i.name.toLowerCase().includes(q) || i.description.toLowerCase().includes(q)
      );
    }
    return list;
  }, [category, search]);

  const connectedList = filtered.filter((i) => connected.includes(i.id));
  const availableList = filtered.filter((i) => !connected.includes(i.id));

  const setField = (id, key, val) =>
    setFormValues((prev) => ({
      ...prev,
      [id]: { ...(prev[id] || {}), [key]: val },
    }));

  const handleConnect = (integration) => {
    const vals = formValues[integration.id] || {};
    const requiredFields = integration.fields.filter((f) => !f.label.includes("optional"));
    const missing = requiredFields.some((f) => !vals[f.key]?.trim());
    if (missing) { toast.error("Please fill in all required fields."); return; }
    setConnected((prev) => [...prev, integration.id]);
    setExpanding(null);
    toast.success(`${integration.name} connected!`);
  };

  const handleDisconnect = (integration) => {
    setConnected((prev) => prev.filter((c) => c !== integration.id));
    toast.success(`${integration.name} disconnected.`);
  };

  const toggleToken = (id, key) =>
    setShowTokens((prev) => ({ ...prev, [`${id}_${key}`]: !prev[`${id}_${key}`] }));

  return (
    <div className="-m-6 flex flex-col" style={{ height: "calc(100vh - 4rem)" }}>

      {/* Sticky header */}
      <div className="shrink-0 bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Integrations</h1>
            <p className="text-sm text-gray-500 mt-0.5">
              {connected.length} of {ALL_INTEGRATIONS.length} integrations connected
            </p>
          </div>
          <div className="relative w-64">
            <FiSearch size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search integrations…"
              className="w-full pl-8 pr-8 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 ring-primary"
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <FiX size={14} />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Sticky category tabs */}
      <div className="shrink-0 bg-white border-b border-gray-200 px-6 py-3">
        <div className="flex items-center border rounded-lg overflow-hidden text-xs font-medium w-fit">
          {CATEGORIES.map((cat) => (
            <button
              key={cat}
              onClick={() => setCategory(cat)}
              className={`shrink-0 flex items-center gap-1.5 px-3 py-1.5 transition ${
                category === cat ? "bg-primary text-white" : "text-gray-500 hover:bg-gray-50"
              }`}
            >
              {cat}
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${
                category === cat ? "bg-white/20 text-white" : "bg-gray-100 text-gray-500"
              }`}>
                {counts[cat]}
              </span>
            </button>
          ))}
        </div>
      </div>

      {loading ? <IntegrationsSkeleton /> : <div className="flex-1 overflow-y-auto bg-gray-50 px-6 py-5 space-y-6">

        {/* Empty search state */}
        {filtered.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center mb-3">
              <FiSearch size={20} className="text-gray-400" />
            </div>
            <p className="text-sm font-medium text-gray-700">No integrations found</p>
            <p className="text-xs text-gray-400 mt-1">Try a different search or category</p>
          </div>
        )}

        {/* Connected section */}
        {connectedList.length > 0 && (
          <section>
            <div className="flex items-center gap-2 mb-3">
              <FiCheck size={14} className="text-green-600" />
              <h2 className="text-sm font-semibold text-gray-700">
                Connected ({connectedList.length})
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
              {connectedList.map((integration) => (
                <IntegrationCard
                  key={integration.id}
                  integration={integration}
                  isConnected
                  isExpanding={false}
                  formValues={formValues[integration.id] || {}}
                  showTokens={showTokens}
                  onExpand={() => {}}
                  onConnect={() => {}}
                  onDisconnect={() => handleDisconnect(integration)}
                  onFieldChange={(key, val) => setField(integration.id, key, val)}
                  onToggleToken={(key) => toggleToken(integration.id, key)}
                />
              ))}
            </div>
          </section>
        )}

        {/* Available section */}
        {availableList.length > 0 && (
          <section>
            {connectedList.length > 0 && (
              <h2 className="text-sm font-semibold text-gray-700 mb-3">
                Available ({availableList.length})
              </h2>
            )}
            <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
              {availableList.map((integration) => (
                <IntegrationCard
                  key={integration.id}
                  integration={integration}
                  isConnected={false}
                  isExpanding={expanding === integration.id}
                  formValues={formValues[integration.id] || {}}
                  showTokens={showTokens}
                  onExpand={() =>
                    setExpanding((prev) =>
                      prev === integration.id ? null : integration.id
                    )
                  }
                  onConnect={() => handleConnect(integration)}
                  onDisconnect={() => {}}
                  onFieldChange={(key, val) => setField(integration.id, key, val)}
                  onToggleToken={(key) => toggleToken(integration.id, key)}
                />
              ))}
            </div>
          </section>
        )}
      </div>}
    </div>
  );
}

function IntegrationCard({
  integration,
  isConnected,
  isExpanding,
  formValues,
  showTokens,
  onExpand,
  onConnect,
  onDisconnect,
  onFieldChange,
  onToggleToken,
}) {
  return (
    <div className={`bg-white border rounded-xl p-5 flex flex-col gap-3 transition-shadow ${
      isConnected ? "border-green-200 shadow-sm" : "border-gray-200 hover:shadow-sm"
    }`}>
      {/* Header row */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-3 min-w-0">
          <IntegrationIcon color={integration.color} initials={integration.initials} />
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <p className="font-semibold text-gray-900 text-sm">{integration.name}</p>
              {integration.popular && (
                <span className="text-xs bg-amber-50 text-amber-700 border border-amber-200 px-1.5 py-0.5 rounded-full leading-none">
                  Popular
                </span>
              )}
              {integration.isNew && (
                <span className="text-xs bg-blue-50 text-blue-700 border border-blue-200 px-1.5 py-0.5 rounded-full leading-none">
                  New
                </span>
              )}
            </div>
            <p className="text-xs text-gray-400 mt-0.5">{integration.category}</p>
          </div>
        </div>

        {isConnected ? (
          <div className="flex items-center gap-1.5 shrink-0">
            <span className="flex items-center gap-1 text-xs text-green-600 font-medium bg-green-50 border border-green-200 px-2 py-1 rounded-lg">
              <FiCheck size={11} /> Connected
            </span>
            <button
              onClick={onDisconnect}
              className="text-xs px-2.5 py-1 border border-red-200 text-red-500 rounded-lg hover:bg-red-50 transition-colors"
            >
              Remove
            </button>
          </div>
        ) : (
          <button
            onClick={onExpand}
            className={`shrink-0 flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg font-medium transition-colors ${
              isExpanding
                ? "bg-gray-100 text-gray-600"
                : "bg-primary text-white hover:opacity-90"
            }`}
          >
            {isExpanding ? (
              <>Cancel</>
            ) : (
              <><FiZap size={11} /> Connect</>
            )}
          </button>
        )}
      </div>

      {/* Description */}
      <p className="text-xs text-gray-500 leading-relaxed">{integration.description}</p>

      {/* Expandable connect form */}
      {isExpanding && !isConnected && (
        <div className="space-y-2.5 pt-3 border-t border-gray-100">
          {integration.fields.map((field) => {
            const isPassword = field.type === "password";
            const isVisible = showTokens[`${field.key}`];
            return (
              <div key={field.key}>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  {field.label}
                </label>
                <div className="relative">
                  <input
                    type={isPassword && !isVisible ? "password" : "text"}
                    placeholder={field.placeholder || ""}
                    value={formValues[field.key] || ""}
                    onChange={(e) => onFieldChange(field.key, e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 ring-primary pr-8"
                  />
                  {isPassword && (
                    <button
                      type="button"
                      onClick={() => onToggleToken(field.key)}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-xs"
                    >
                      {isVisible ? "Hide" : "Show"}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
          <button
            onClick={onConnect}
            className="w-full mt-1 bg-primary text-white py-2 rounded-lg text-sm font-medium hover:opacity-90 transition-opacity"
          >
            Save & Connect
          </button>
        </div>
      )}
    </div>
  );
}
